/* eslint-disable check-i18n-id-prefix/i18n-id-prefix-with-package-name-FormattedMessage */

/*
This component represents the help center list item in the editor help menu
(currently in the bottom left of the editor view).

It is broken out as a component because of the need to add/remove a listener in
componentDidUpdate.
 */

import React, { type FC, useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import AkDropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';

import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext.ts';

import { HelpMenuClassicContext } from './HelpMenuClassicContext.tsx';
import { HELP_MENU_SPOTLIGHT } from './constants.ts';
import { useHelpMenuState } from './useHelpMenuState.tsx';

type AnyFunctionType = (...args: any[]) => any;

export type HelpMenuHelpCenterItemProps = {
	closeHelpMenu: AnyFunctionType;
};

type HelpMenuHelpCenterItemContextProps = {
	Changeboarding: any;
};

const HelpMenuHelpCenterItemWithContext = (
	props: HelpMenuHelpCenterItemProps & WithAnalyticsEventsProps,
) => {
	const classicEditorContext = useContext(ClassicEditorContext);
	const helpMenuClassicContext = useContext(HelpMenuClassicContext);

	// The next editor uses the ClassicEditorContext, while classic uses the HelpMenuClassicContext
	const { Changeboarding } = classicEditorContext.Changeboarding
		? classicEditorContext
		: helpMenuClassicContext!;

	return <HelpMenuHelpCenterItemClass {...props} Changeboarding={Changeboarding} />;
};

const HelpMenuHelpCenterItemClass: FC<
	HelpMenuHelpCenterItemProps & WithAnalyticsEventsProps & HelpMenuHelpCenterItemContextProps
> = ({ closeHelpMenu, createAnalyticsEvent, Changeboarding }) => {
	const [helpCenterSweetState, { openHelpCenter, hideHelpMenuSpotlight }] = useHelpMenuState();

	const onOpenHelpCenter = () => {
		openHelpCenter();
		closeHelpMenu();

		createAnalyticsEvent!({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'helpMenuItemForEditorHelpCenter',
				source: 'editorHelpMenu',
			},
		}).fire();
	};

	/**
	 * When the last changeboard in the current sequence is closed, call this
	 * callback to set the state of the spotlight in helpCenterState.
	 * @param helpCenterState
	 */
	const changeboardingFinished = () => {
		hideHelpMenuSpotlight();
		closeHelpMenu();

		createAnalyticsEvent!({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'spotlightForEditorHelpCenter',
				source: 'editor help menu spotlight',
			},
		}).fire();
	};

	const isHelpCenterOpen = helpCenterSweetState.isHelpCenterOpen;

	return helpCenterSweetState.shouldShowHelpMenuSpotlight ? (
		<Changeboarding
			spotlightId={HELP_MENU_SPOTLIGHT}
			delaySpotlight={200}
			changeboardFlowFinished={changeboardingFinished}
		>
			<AkDropdownItem isDisabled={isHelpCenterOpen} onClick={onOpenHelpCenter}>
				<FormattedMessage id="fabric.help.center.menuitem.title" />
			</AkDropdownItem>
		</Changeboarding>
	) : (
		<AkDropdownItem
			testId="help-center-menuitem-title"
			isDisabled={isHelpCenterOpen}
			onClick={onOpenHelpCenter}
		>
			<FormattedMessage id="fabric.help.center.menuitem.title" />
		</AkDropdownItem>
	);
};

export const HelpMenuHelpCenterItem: React.ForwardRefExoticComponent<
	Omit<HelpMenuHelpCenterItemProps, keyof WithAnalyticsEventsProps> & React.RefAttributes<any>
> = withAnalyticsEvents()(HelpMenuHelpCenterItemWithContext);
